import { useCallback, useEffect, useMemo, useState } from "react";
import type { AudienceAge, RealmId, UserProfile } from "@chuzi/shared/types";
import { t } from "@chuzi/shared/config";
import { ChuziApiError, isInsufficientCreditsError } from "@chuzi/shared/api";
import { CHUZI_AI_GENERATOR_VIBES, CHUZI_AI_VIBE_LABELS, AUDIENCE_AGE_OPTIONS } from "@chuzi/shared/config";
import { api } from "../lib/api";
import { applyChuziAiStory } from "../lib/chuzi-ai-scene-builder";
import { persistStoryGeneratorContext, loadGeneratorPrefs } from "../lib/story-generator-context";
import { AddCreditsButton } from "./add-credits-button";
import { handleInsufficientCredits, useSpendingModal } from "../lib/spending-modal";
import { useDebouncedValue } from "../lib/use-debounced-value";
import { SpeechTextarea } from "./speech-textarea";

interface StoryGeneratorProps {
  realm: RealmId;
  storyId: string;
  storyTitle: string;
  user?: UserProfile | null;
  onComplete: (storyId: string, firstSceneId: string | null) => void;
  onCancel: () => void;
}

export function StoryGenerator({
  realm,
  storyId,
  storyTitle,
  user = null,
  onComplete,
  onCancel,
}: StoryGeneratorProps) {
  const { openSpendingModal } = useSpendingModal();
  const savedPrefs = useMemo(() => loadGeneratorPrefs(), []);
  const [selectedVibes, setSelectedVibes] = useState<Set<string>>(
    () => new Set(savedPrefs?.vibes ?? []),
  );
  const [audienceAge, setAudienceAge] = useState<AudienceAge | "">(
    savedPrefs?.audience_age ?? "",
  );
  const [details, setDetails] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canAfford, setCanAfford] = useState(true);
  const [creditsNeeded, setCreditsNeeded] = useState(1);
  const debouncedDetails = useDebouncedValue(details, 400);
  const estimateVibes = useMemo(
    () => Array.from(selectedVibes).sort(),
    [selectedVibes],
  );

  const refreshAffordability = useCallback(() => {
    if (estimateVibes.length === 0) {
      setCanAfford(true);
      setCreditsNeeded(1);
      return;
    }

    let cancelled = false;
    api.ai
      .storyEstimate({
        vibes: estimateVibes,
        details: debouncedDetails.trim() || undefined,
        max_scenes: 1,
        audience_age: audienceAge || undefined,
      })
      .then((res) => {
        if (cancelled) return;
        setCanAfford(!!user?.is_admin || res.can_afford);
        setCreditsNeeded(res.estimate.credits_needed);
      })
      .catch(() => {
        if (!cancelled) {
          setCanAfford(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [estimateVibes, debouncedDetails, audienceAge, user?.is_admin]);

  useEffect(() => {
    const cleanup = refreshAffordability();
    return cleanup;
  }, [refreshAffordability]);

  useEffect(() => {
    function onCreditsUpdated() {
      refreshAffordability();
    }
    window.addEventListener("chuzi:credits-updated", onCreditsUpdated);
    return () => window.removeEventListener("chuzi:credits-updated", onCreditsUpdated);
  }, [refreshAffordability]);

  function toggleVibe(vibe: string) {
    setSelectedVibes((prev) => {
      const next = new Set(prev);
      if (next.has(vibe)) next.delete(vibe);
      else next.add(vibe);
      return next;
    });
  }

  async function handleGenerate() {
    if (selectedVibes.size === 0) {
      setError(t(realm, "generator_vibes_required", "Pick at least one vibe."));
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const res = await api.ai.storyGenerate({
        vibes: Array.from(selectedVibes),
        details: details.trim() || undefined,
        max_scenes: 1,
        audience_age: audienceAge || undefined,
      });
      persistStoryGeneratorContext(storyId, {
        vibes: Array.from(selectedVibes),
        details: details.trim(),
        audience_age: audienceAge || undefined,
      });
      const result = await applyChuziAiStory(res.generation, storyId);
      onComplete(storyId, result.contentSceneIds[0] ?? null);
    } catch (err) {
      const opened = await handleInsufficientCredits(
        err,
        user,
        openSpendingModal,
        () => handleGenerate(),
      );
      if (opened) {
        setGenerating(false);
        return;
      }

      let msg = t(realm, "chuzi_ai_generate_failed");
      if (err instanceof ChuziApiError) {
        if (err.status === 429) {
          setError(t(realm, "error_rate_limited"));
          setGenerating(false);
          return;
        }
        const apiBody = err.body as
          | { error?: string; message?: string; errors?: Record<string, string[]> }
          | null;
        if (isInsufficientCreditsError(err)) {
          setGenerating(false);
          return;
        }
        const firstField = apiBody?.errors
          ? Object.values(apiBody.errors).flat()[0]
          : null;
        msg = firstField ?? apiBody?.error ?? apiBody?.message ?? msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setError(msg);
      setGenerating(false);
    }
  }

  const accent = "rgba(180,100,255,0.5)";
  const accentBg = "rgba(180,100,255,0.15)";
  const border = "rgba(180,100,255,0.35)";
  const panelBg = "rgba(10,14,28,0.95)";

  const generateBtnStyle: React.CSSProperties = {
    flex: 1,
    padding: "9px 0",
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    background: accentBg,
    border: `1px solid ${accent}`,
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          width: 420,
          maxHeight: "min(88vh, 620px)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: 16,
          background: panelBg,
          border: `1px solid ${border}`,
          borderRadius: 10,
          overflowY: "auto",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: accent }}>
          {t(realm, "chuzi_ai_name")}
          {" · "}
          {t(realm, "chuzi_ai_generate")}
        </div>

        {generating ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 16,
              padding: 40,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "3px solid rgba(180,100,255,0.3)",
                borderTopColor: "#b464ff",
                animation: "chuzi-spin 0.8s linear infinite",
              }}
            />
            <div style={{ color: "rgba(232,240,255,0.7)", fontSize: 12 }}>
              {t(realm, "chuzi_ai_generating")}
            </div>
            <style>{`@keyframes chuzi-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#e8f0ff" }}>
              {t(realm, "generator_vibes_prompt")}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {CHUZI_AI_GENERATOR_VIBES.map((vibe) => {
                const isSelected = selectedVibes.has(vibe);
                return (
                  <button
                    key={vibe}
                    type="button"
                    onClick={() => toggleVibe(vibe)}
                    style={{
                      padding: "4px 10px",
                      fontSize: 10,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      borderRadius: 20,
                      cursor: "pointer",
                      color: isSelected ? "#fff" : "rgba(232,240,255,0.7)",
                      background: isSelected ? accentBg : "rgba(232,240,255,0.05)",
                      border: `1px solid ${isSelected ? accent : "rgba(232,240,255,0.15)"}`,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {CHUZI_AI_VIBE_LABELS[vibe]}
                  </button>
                );
              })}
            </div>

            <div style={{ fontSize: 11, fontWeight: 600, color: "#e8f0ff", marginTop: 2 }}>
              {t(realm, "generator_audience_prompt")}
            </div>
            <select
              value={audienceAge}
              onChange={(e) => setAudienceAge(e.target.value as AudienceAge | "")}
              style={{
                width: "100%",
                padding: "6px 10px",
                fontSize: 11,
                fontFamily: "inherit",
                color: audienceAge ? "#e8f0ff" : "rgba(232,240,255,0.45)",
                background: "rgba(180,100,255,0.08)",
                border: `1px solid ${border}`,
                borderRadius: 6,
                outline: "none",
                cursor: "pointer",
                appearance: "none",
                WebkitAppearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' stroke='rgba(232,240,255,0.5)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
              }}
            >
              <option value="" style={{ background: "#0a0e1c", color: "rgba(232,240,255,0.45)" }}>
                {t(realm, "generator_audience_placeholder")}
              </option>
              {AUDIENCE_AGE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id} style={{ background: "#0a0e1c", color: "#e8f0ff" }}>
                  {opt.label} — {opt.description}
                </option>
              ))}
            </select>

            <div style={{ fontSize: 11, fontWeight: 600, color: "#e8f0ff", marginTop: 2 }}>
              {t(realm, "generator_details_prompt")}
            </div>
            <SpeechTextarea
              value={details}
              onChange={setDetails}
              placeholder={t(realm, "generator_details_placeholder")}
              autoFocus
              accent="rgba(180,100,255,0.9)"
              accentBg={accentBg}
              border={border}
              background="rgba(180,100,255,0.08)"
              minHeight={80}
              style={{ fontSize: 12, padding: "8px 10px" }}
            />
          </>
        )}

        {error ? (
          <div style={{ fontSize: 11, color: "#ff9aa2" }}>{error}</div>
        ) : null}

        {!generating && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                flex: 1,
                padding: "7px 0",
                fontSize: 11,
                fontWeight: 600,
                color: "rgba(232,240,255,0.6)",
                background: "transparent",
                border: `1px solid ${border}`,
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t(realm, "chuzi_ai_back")}
            </button>
            <AddCreditsButton
              realm={realm}
              pool="create"
              creditsNeeded={creditsNeeded}
              canAfford={canAfford}
              user={user}
              label={t(realm, "chuzi_ai_generate")}
              disabled={selectedVibes.size === 0}
              onAction={() => void handleGenerate()}
              onCreditsAdded={refreshAffordability}
              style={generateBtnStyle}
            />
          </div>
        )}
      </div>
    </div>
  );
}
